import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import Input from "../../components/form/input/InputField";
import Select from "../../components/form/Select";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import { FormInputEvent } from '../../types/form';
import { SelectOption } from '../../types/select';

interface AdminUserAddress {
  id: string;
  adminUserId: string;
  addressType: string;
  street: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  country: string;
  latitude?: number | null;
  longitude?: number | null;
  isDefault: boolean;
}

interface AdminUser {
  id: string;
  fullName: string;
}

const Label = ({ htmlFor, text }: { htmlFor: string; text: string }) => (
  <label htmlFor={htmlFor} className="block text-sm font-medium text-gray-700">
    {text}
  </label>
);

export default function AdminUserAddressForm() {
  const { addressId } = useParams<{ addressId?: string }>();
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUser[]>([]);

  const [address, setAddress] = useState<AdminUserAddress>({
    id: "",
    adminUserId: "",
    addressType: "",
    street: "",
    city: "",
    stateProvince: "",
    postalCode: "",
    country: "",
    latitude: null,
    longitude: null,
    isDefault: false
  });

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    fetchUsers();
    if (addressId) {
      fetchAddress();
    }
  }, [addressId]);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get("/AdminUser/getall");
      setUsers(response.data?.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchAddress = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/AdminUserAddress/get/${addressId}`);
      setAddress(response.data);
    } catch (error) {
      setError("Failed to load address data.");
      console.error("Error fetching address:", error);
    }
    setLoading(false);
  };

  const handleChange = (e: FormInputEvent) => {
    const { name, value } = e.target;
    setAddress(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (field: string, value: string) => {
    setAddress(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!address.adminUserId || !address.street || !address.city || !address.country) {
      setError("Please fill in all required fields.");
      return;
    }

    setLoading(true);
    try {
      if (addressId) {
        await axiosInstance.put(`/AdminUserAddress/update/${addressId}`, address);
      } else {
        await axiosInstance.post(`/AdminUserAddress/add`, address);
      }
      navigate("/admin-user-address");
    } catch (error) {
      setError("Error saving address. Please try again.");
      console.error("Save error:", error);
    }
    setLoading(false);
  };

  return (
   
      <div className="space-y-6 w-full">
        <PageBreadcrumb pageTitle={addressId ? "Edit Address" : "Add Address"} />

        {loading ? (
          <p className="text-center text-gray-500">Loading...</p>
        ) : (
          <form onSubmit={handleSubmit} className="mt-6">
            {error && (
              <div className="mb-4 rounded-xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-500/30 dark:bg-error-500/10 dark:text-error-400">
                {error}
              </div>
            )}

            <div className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="adminUserId" text="User *" />
                <Select
                  label="User"
                  name="adminUserId"
                  placeholder="Select User"
                  value={address.adminUserId}
                  onChange={(value: string) => handleSelectChange("adminUserId", value)}
                  options={users.map((user): SelectOption => ({
                    value: user.id,
                    label: user.fullName
                  }))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="addressType" text="Address Type *" />
                <Select
                  label="Address Type"
                  name="addressType"
                  placeholder="Select Type"
                  value={address.addressType}
                  onChange={(value: string) => handleSelectChange("addressType", value)}
                  options={[
                    { value: "Home", label: "Home" },
                    { value: "Work", label: "Work" },
                    { value: "Other", label: "Other" }
                  ]}
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="street" text="Street Address *" />
                <Input
                  name="street"
                  value={address.street}
                  onChange={handleChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="city" text="City *" />
                <Input
                  name="city"
                  value={address.city}
                  onChange={handleChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="stateProvince" text="State/Province" />
                <Input
                  name="stateProvince"
                  value={address.stateProvince}
                  onChange={handleChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="postalCode" text="Postal Code" />
                <Input
                  name="postalCode"
                  value={address.postalCode}
                  onChange={handleChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="country" text="Country *" />
                <Input
                  name="country"
                  value={address.country}
                  onChange={handleChange}
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="isDefault"
                    checked={address.isDefault}
                    onChange={handleChange}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Set as default address
                  </span>
                </label>
              </div>
            </div>

            <div className="mt-8 flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate("/admin-user-address")}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-brand-500 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {addressId ? "Update Address" : "Add Address"}
              </button>
            </div>
          </form>
        )}
      </div>
  );
} 